import { HttpClient } from '@zeta-core';
import {
  APIResponse,
  IAddExternaTaskDetailRequest,
  IAddExternaTaskDetailResponse,
  IAddMeetingDetailsRequest,
  IAddMeetingDetailsResponse,
  IAssetAllocation,
  IClientAssetResponse,
  ICreateCustomTaskRequest,
  ICreateCustomTaskResponse,
  ICreatePreSaleQuoteRequest,
  ICreatePreSaleQuoteResponse,
  ICreateRecommendationResponse,
  ICreateRecommendationTask,
  IDocumentUploadPayload,
  IEmptyRequestResponse,
  IGetClientAndPartnerDetailsRequest,
  IGetClientAndPartnerDetailsResponse,
  IGetCustomTaskDetailsResponse,
  IGetFinancialGoalsResponse,
  IGetFinancialPlanTypesRequest,
  IGetFinancialPlanTypesResponse,
  IGetInsuranceTypeRequest,
  IGetInvestmentDetailsRequest,
  IGetMeetingDetailsResponse,
  IGetParaplanningSubTaskResponse,
  IGetParaplanningTaskResponse,
  IGetPortfoliosCountResponse,
  IGetPortfoliosProvidersResponse,
  IGetRecommendationsRequest,
  IGetStartedWithTaskResponse,
  IGetTaskCommentsResponse,
  IGoalContribution,
  IGoalData,
  IGoalProjection,
  IObjective,
  IPot,
  IProtectionPlanningTaskRequest,
  IProtectionPlanningTaskResponse,
  IQuotationUpdatePayload,
  IRecommendationReportResponse,
  ISaveInvestmentRecommendation,
  ISearchTaskPayload,
  ISearchTaskResponse,
  ISimulatedTransaction,
  ITaskDetailsResponse,
  IUpdateParaplanningTakRequest,
  IUpdateProtectionAssessmentRequest,
  IUpdateProtectionAssessmentResponse,
  IUpdateProtectionPlanSelectionRequest,
  IUpdateProtectionPlanSelectionResponse,
  IUpdateTaskresponse,
  IUploadProductSelectionRequest,
  IUploadProductSelectionResponse,
  PersonalDetailsClient,
  QuotationUpdateRequest,
  TaskInstanceList,
} from '@zeta-types';
import { AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import { InvestorService } from '../investor-service';
import { FINANCIAL_PLANNING_SERVICE } from '../services-path';

export const FinancialPlanningService = () => {
  return {
    searchTasks: (payload: ISearchTaskPayload): APIResponse<ISearchTaskResponse> => HttpClient.post(`${FINANCIAL_PLANNING_SERVICE}/task/search`, payload),

    createSolutionTask: (payload: IProtectionPlanningTaskRequest) => {
      return HttpClient.post<IProtectionPlanningTaskResponse>(`${FINANCIAL_PLANNING_SERVICE}/solution/task`, payload);
    },

    createParaplanningTask: (payload: IProtectionPlanningTaskRequest) => {
      return HttpClient.post<IProtectionPlanningTaskResponse>(`${FINANCIAL_PLANNING_SERVICE}/paraplanning/task`, payload);
    },

    createExecutionTask: (payload: IProtectionPlanningTaskRequest) => {
      return HttpClient.post<IProtectionPlanningTaskResponse>(`${FINANCIAL_PLANNING_SERVICE}/execution/task`, payload);
    },

    getTaskById: (taskId: string, payload: IEmptyRequestResponse) => {
      return HttpClient.get<ITaskDetailsResponse>(`${FINANCIAL_PLANNING_SERVICE}/task/${taskId}`, payload);
    },

    getStartedWithTaskById: (taskId: string, payload: IEmptyRequestResponse) => {
      return HttpClient.put<IGetStartedWithTaskResponse>(`${FINANCIAL_PLANNING_SERVICE}/task/${taskId}/get-started`, payload);
    },

    getSolutionTask: (payload: { taskId: string }) => {
      return HttpClient.get<ITaskDetailsResponse>(`${FINANCIAL_PLANNING_SERVICE}/solution/task/${payload?.taskId}/sub-task`, {});
    },

    updateProtectionAssessment: (payload: IUpdateProtectionAssessmentRequest) => {
      return HttpClient.put<IUpdateProtectionAssessmentResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/assessment/${payload?.taskId}/${payload?.assessmentId}`,
        payload
      );
    },

    updateProtectionPlanSelection: (payload: IUpdateProtectionPlanSelectionRequest) => {
      return HttpClient.put<IUpdateProtectionPlanSelectionResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/protectionplan/task/${payload?.taskId}/sub-task/${payload?.protectionPlanId}`,
        payload
      );
    },

    getClientandPartnerDetails: async (payload: IGetClientAndPartnerDetailsRequest): APIResponse<IGetClientAndPartnerDetailsResponse> => {
      // Client metadata is owned by investor; the financial-planning lookup was decommissioned.
      const response = await InvestorService().viewPersonalDetails({ clientID: payload.clientId });
      const { clientDetails, partnerDetails } = response.data;
      const toDetail = (person?: PersonalDetailsClient) => {
        if (!person) return undefined;
        const personal = person.personalPiiDetails;
        return {
          id: personal?.clientID || person.id || '',
          title: personal?.title || '',
          name: [personal?.firstName, personal?.lastName].filter(Boolean).join(' '),
        };
      };
      // The investor response is relative to the signed-in client in the client portal.
      const [client, partner] = clientDetails?.clientType === 'Partner' ? [partnerDetails, clientDetails] : [clientDetails, partnerDetails];
      return { ...response, data: { clientDetail: toDetail(client), partnerDetail: toDetail(partner) } };
    },

    getClientandPartnerDetailsForClient: (payload: IGetClientAndPartnerDetailsRequest) => {
      return HttpClient.get<IGetClientAndPartnerDetailsResponse>(`${FINANCIAL_PLANNING_SERVICE}/clients/${payload?.clientId}`, {});
    },

    getInsuranceType: (payload: IGetInsuranceTypeRequest) => {
      return HttpClient.get<string[]>(`${FINANCIAL_PLANNING_SERVICE}/quotation/task/${payload?.taskId}/insuranceType`, {});
    },

    uploadProductSelection: (
      payload: IUploadProductSelectionRequest,
      config: AxiosRequestConfig,
      taskId: string
    ): APIResponse<IUploadProductSelectionResponse> =>
      HttpClient.upload(`${FINANCIAL_PLANNING_SERVICE}/quotation/task/${taskId}/preview-upload`, payload, config),

    updateQuotationSelection: (payload: IQuotationUpdatePayload, taskId: string) => {
      return HttpClient.put<QuotationUpdateRequest[]>(`${FINANCIAL_PLANNING_SERVICE}/quotation/task/${taskId}`, payload);
    },

    updateParaplanningTask: (taskId: string, paraPlanningId: string, payload: IUpdateParaplanningTakRequest) => {
      return HttpClient.put<IGetParaplanningTaskResponse>(`${FINANCIAL_PLANNING_SERVICE}/paraplanning/task/${taskId}/sub-task/${paraPlanningId}`, payload);
    },

    getParaplanningTask: (caseId: string, name: string, payload: IEmptyRequestResponse) => {
      return HttpClient.get<IGetParaplanningSubTaskResponse>(`${FINANCIAL_PLANNING_SERVICE}/case/summary/${caseId}/sub-task/${name}`, payload);
    },

    getSubTaskDetails: (caseId: string, subTaskName: string) => {
      return HttpClient.get<IGetParaplanningSubTaskResponse>(`${FINANCIAL_PLANNING_SERVICE}/case/summary/${caseId}/sub-task/${subTaskName}`);
    },

    updateSubTaskDetails: (taskId: string, subTaskName: string, subTaskId: string, payload: IUpdateParaplanningTakRequest) => {
      return HttpClient.put<IGetParaplanningTaskResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/execution/task/${taskId}/sub-task/${subTaskName}/${subTaskId}`,
        payload
      );
    },

    uploadDocument: (
      taskId: string,
      payload: IDocumentUploadPayload,
      onUploadProgress: (progressEvent: AxiosProgressEvent) => void
    ): APIResponse<IEmptyRequestResponse> => HttpClient.upload(`${FINANCIAL_PLANNING_SERVICE}/document/task/${taskId}/upload`, payload, { onUploadProgress }),

    downloadDocumentById: (taskId: string, documentId: string, config: AxiosRequestConfig): APIResponse<Blob> =>
      HttpClient.download(`${FINANCIAL_PLANNING_SERVICE}/document/task/${taskId}/download/${documentId}`, {}, config),

    updateTaskStatus: (taskId: string, sectionName: string, payload: IEmptyRequestResponse) => {
      return HttpClient.put<IUpdateTaskresponse>(`${FINANCIAL_PLANNING_SERVICE}/task/${taskId}/sub-task/${sectionName}`, payload);
    },

    getTaskComments: (taskId: string, sectionName: string) => {
      return HttpClient.get<IGetTaskCommentsResponse[]>(`${FINANCIAL_PLANNING_SERVICE}/comments/${taskId}/${sectionName}`);
    },

    addMeetingDetails: (payload: IAddMeetingDetailsRequest, taskId: string) => {
      return HttpClient.post<IAddMeetingDetailsResponse>(`${FINANCIAL_PLANNING_SERVICE}/scheduleMeeting/task/${taskId}/sub-task`, payload);
    },

    getMeetingDetails: (taskId: string) => {
      return HttpClient.get<IGetMeetingDetailsResponse[]>(`${FINANCIAL_PLANNING_SERVICE}/scheduleMeeting/task/${taskId}/sub-task`);
    },

    getRecommendationTask: (taskId: string) => {
      return HttpClient.get<IRecommendationReportResponse>(`${FINANCIAL_PLANNING_SERVICE}/recommendation/task/${taskId}/sub-task`);
    },

    generateRecommendationReport: (taskId: string) => {
      return HttpClient.get<IRecommendationReportResponse>(`${FINANCIAL_PLANNING_SERVICE}/recommendation/task/${taskId}/generateReport`);
    },

    sendToClientRecommendationReport: (taskId: string) => {
      return HttpClient.get<IRecommendationReportResponse>(`${FINANCIAL_PLANNING_SERVICE}/recommendation/task/${taskId}/sendToClient`);
    },

    downloadRecommendationReport: (taskId: string) => {
      return HttpClient.download<IRecommendationReportResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/recommendation/task/${taskId}/report-download`,
        {},
        { method: 'GET' }
      );
    },

    getPreSaleQuote: (taskInstanceId: string) => {
      return HttpClient.get<TaskInstanceList>(`${FINANCIAL_PLANNING_SERVICE}/pre-sale-quotes/task/${taskInstanceId}`);
    },

    createPreSaleQuote: (payload: ICreatePreSaleQuoteRequest, taskInstanceId: string, update: boolean) => {
      if (update) {
        return HttpClient.put<ICreatePreSaleQuoteResponse>(`${FINANCIAL_PLANNING_SERVICE}/pre-sale-quotes/task/${taskInstanceId}`, payload);
      }
      return HttpClient.post<ICreatePreSaleQuoteResponse>(`${FINANCIAL_PLANNING_SERVICE}/pre-sale-quotes/task/${taskInstanceId}`, payload);
    },

    createCustomTask: (payload: ICreateCustomTaskRequest) => {
      return HttpClient.post<ICreateCustomTaskResponse>(`${FINANCIAL_PLANNING_SERVICE}/custom-tasks`, payload);
    },

    addExternalTaskDetail: (payload: IAddExternaTaskDetailRequest) => {
      return HttpClient.post<IAddExternaTaskDetailResponse>(`${FINANCIAL_PLANNING_SERVICE}/external-tasks-detail`, payload);
    },

    getExternalTaskDetails: (externalTaskDetailId: string) => {
      return HttpClient.get<IGetCustomTaskDetailsResponse>(`${FINANCIAL_PLANNING_SERVICE}/external-tasks-detail/${externalTaskDetailId}`);
    },

    uploadCustomTaskDocument: (externalTaskDetailId: string, payload, config) => {
      return HttpClient.upload<IGetCustomTaskDetailsResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/external-tasks-detail/${externalTaskDetailId}/document`,
        payload,
        config
      );
    },

    downloadCustomTaskDocument: (externalTaskDetailId: string, documentId: string) => {
      return HttpClient.download<IGetCustomTaskDetailsResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/external-tasks-detail/${externalTaskDetailId}/document/${documentId}`,
        {},
        { method: 'GET' },
        false,
        true
      );
    },

    deleteCustomTaskDocument: (externalTaskDetailId: string, documentId: string) => {
      return HttpClient.delete<IGetCustomTaskDetailsResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/external-tasks-detail/${externalTaskDetailId}/document/${documentId}`
      );
    },

    saveCustomTask: (payload: ICreateCustomTaskRequest, externalTaskDetailId: string) => {
      return HttpClient.put<ICreateCustomTaskResponse>(`${FINANCIAL_PLANNING_SERVICE}/external-tasks-detail/${externalTaskDetailId}`, payload);
    },

    getInvestmentDetails: (payload: IGetInvestmentDetailsRequest) => {
      return HttpClient.get<IClientAssetResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/investment-accounts/${payload?.clientId}?accountType=${payload?.accountType}&disableWorkflow=true`,
        {}
      );
    },

    getInvestmentDetailsUsingRecommendationId: (payload: IGetInvestmentDetailsRequest) => {
      return HttpClient.get<IClientAssetResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/investment-accounts/${payload?.clientId}/recommendation/${payload?.recommendationId}?disableWorkflow=true`,
        {}
      );
    },

    getRecommendationDetails: (payload: IGetRecommendationsRequest) => {
      return HttpClient.get<string[]>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/task/${payload?.taskInstanceId}`, {});
    },

    getPortfolioPlatforms: () => {
      return HttpClient.get<string[]>(`${FINANCIAL_PLANNING_SERVICE}/portfolios/provider`, {});
    },

    getPortfolioProviders: () => {
      return HttpClient.get<IGetPortfoliosProvidersResponse>(`${FINANCIAL_PLANNING_SERVICE}/portfolios/provider`);
    },

    getPortfolioCount: () => {
      return HttpClient.get<IGetPortfoliosCountResponse>(`${FINANCIAL_PLANNING_SERVICE}/portfolios/count`);
    },

    saveInvestmentRecommendation: (payload: ISaveInvestmentRecommendation, update: boolean, taskInstanceId: string, recommendationId?: string) => {
      if (update) {
        return HttpClient.put<ISaveInvestmentRecommendation>(
          `${FINANCIAL_PLANNING_SERVICE}/investment-planning/task/${taskInstanceId}/recommendation/${recommendationId}`,
          payload
        );
      }
      return HttpClient.post<ISaveInvestmentRecommendation>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/task/${taskInstanceId}`, payload);
    },

    getAccountTypeDetails: () => {
      return HttpClient.get<string[]>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/investment-accounts/type`, {});
    },

    updateTaskStatusApi: (payload: ISaveInvestmentRecommendation, taskInstanceId: string) => {
      return HttpClient.put<ISaveInvestmentRecommendation>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskInstanceId}`, payload);
    },

    deleteRecommendation: (payload) => {
      return HttpClient.delete<IEmptyRequestResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/task/${payload?.taskInstanceId}/recommendation/${payload?.recommendationId}`
      );
    },

    getTaskStatus: (payload: IGetRecommendationsRequest) => {
      return HttpClient.get<string[]>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${payload?.taskInstanceId}/status`, {});
    },

    getPotTypes: () => {
      return HttpClient.get<string[]>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/investment-accounts`, {});
    },

    getPotCategoryOptions: () => {
      return HttpClient.get<string[]>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/pot-categories`, {});
    },

    getRecommendationTaskDetails: (taskId: string) => {
      return HttpClient.get<ICreateRecommendationResponse>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}`, {});
    },

    createRecommendationTask: (payload: ICreateRecommendationTask) => {
      return HttpClient.post<ICreateRecommendationResponse>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task`, payload);
    },

    savePot: (payload: any, taskId: string, potId: string, update: boolean) => {
      if (update) {
        return HttpClient.put<IPot>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/pots/${potId}`, payload);
      }
      return HttpClient.post<IPot>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/pots`, payload);
    },

    getPots: (taskId: string) => {
      return HttpClient.get<string[]>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/pots`, {});
    },

    updateGoal: (payload: IGoalData, taskId: string, goalId: string) => {
      return HttpClient.put<IGoalData>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/goals/${goalId}`, payload);
    },

    assetAllocationUpdate: (payload: IAssetAllocation, taskId: string, goalId: string) => {
      return HttpClient.put<IAssetAllocation>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/goals/${goalId}/pot-allocations/upsert`,
        payload
      );
    },

    simulatedTransactionsUpdate: (payload: ISimulatedTransaction, taskId: string, goalId: string) => {
      return HttpClient.put<ISimulatedTransaction>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/transactions/upsert`,
        payload
      );
    },

    fetchGoalProjections: (taskId: string, goalId: string) => {
      return HttpClient.get<IGoalProjection>(`${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/goals/${goalId}/projections`, {});
    },

    fetchContributions: (taskId: string, potId: string) => {
      return HttpClient.get<IGoalContribution[]>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/pots/${potId}/pot-allocations/contribution-amount`,
        {}
      );
    },

    fetchAllocationContributions: (taskId: string, potId: string) => {
      return HttpClient.get<IGoalContribution[]>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/pots/${potId}/pot-allocations/pot-investment-amount`,
        {}
      );
    },

    getPotContributionValidations: (taskId: string) => {
      return HttpClient.get<IGoalContribution[]>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${taskId}/validatePotContributions`,
        {}
      );
    },

    deletePot: (payload) => {
      return HttpClient.delete<IEmptyRequestResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${payload?.taskId}/pots/${payload?.potId}`
      );
    },

    deleteAssetAllocation: (payload) => {
      return HttpClient.delete<IEmptyRequestResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${payload?.taskId}/goals/${payload?.goalId}/pot-allocations/${payload?.potAllocationId}`
      );
    },

    deleteSimulatedTransactions: (payload) => {
      return HttpClient.delete<IEmptyRequestResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${payload?.taskId}/transactions/${payload?.transactionId}`
      );
    },

    deleteSimulatedTransaction: (payload) => {
      return HttpClient.delete<IEmptyRequestResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/investment-planning/recommendation-task/${payload?.taskId}/pots/${payload?.potId}/goals/${payload?.goalId}/transactions/${payload?.transactionId}`
      );
    },

    getGoalsList: ({ clientId, caseId }: { clientId: string; caseId: string }) => {
      return HttpClient.get<IGetFinancialGoalsResponse[]>(`${FINANCIAL_PLANNING_SERVICE}/clients/${clientId}/cases/${caseId}/financial-planning-summary`, {});
    },

    getRecommendation: ({
      clientId,
      payload,
    }: {
      clientId: string;
      payload: {
        objectives: IObjective[];
      };
    }) => {
      return HttpClient.post<IGetFinancialGoalsResponse[]>(`${FINANCIAL_PLANNING_SERVICE}/clients/${clientId}/plans`, payload);
    },

    // FOR MANAGED CLIENT
    getProtectPlanningDetails: (payload, clientId: string) => {
      return HttpClient.post<TaskInstanceList>(`${FINANCIAL_PLANNING_SERVICE}/advisor/clients/${clientId}/protection-planning`, payload);
    },

    getInvestmentPlanDetails: (clientId: string) => {
      return HttpClient.get<TaskInstanceList>(`${FINANCIAL_PLANNING_SERVICE}/advisor/clients/${clientId}/investment-planning`);
    },

    getFinancialPlanTypes: (payload: IGetFinancialPlanTypesRequest) => {
      return HttpClient.get<IGetFinancialPlanTypesResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/advisor/clients/${payload.clientId}/cases/${payload.caseId}/financial-plan-types`
      );
    },

    // managed client - client portal

    getProtectPlanningDetailsForClient: (payload, clientId: string) => {
      return HttpClient.post<TaskInstanceList>(`${FINANCIAL_PLANNING_SERVICE}/clients/${clientId}/protection-planning`, payload);
    },

    getInvestmentPlanDetailsForClient: (clientId: string) => {
      return HttpClient.get<TaskInstanceList>(`${FINANCIAL_PLANNING_SERVICE}/clients/${clientId}/investment-planning`);
    },

    getFinancialPlanTypesForClient: (payload: IGetFinancialPlanTypesRequest) => {
      return HttpClient.get<IGetFinancialPlanTypesResponse>(
        `${FINANCIAL_PLANNING_SERVICE}/clients/${payload.clientId}/cases/${payload.caseId}/financial-plan-types`
      );
    },

    fetchContributionsForClient: (clientId: string, taskId: string, potId: string) => {
      return HttpClient.get<IGoalContribution[]>(
        `${FINANCIAL_PLANNING_SERVICE}/clients/${clientId}/investment-planning/recommendation-task/${taskId}/pots/${potId}/pot-allocations/contribution-amount`,
        {}
      );
    },

    getOwnerTypeForClient: (clientId: string) => {
      return HttpClient.get<string[]>(`${FINANCIAL_PLANNING_SERVICE}/clients/${clientId}`, {});
    },
  };
};
